// OSPROD-MAIL-CONNECT-MODAL: success modal shown right after the
// Google OAuth callback (`?connected=ok&accountId=…` redirect-back).
//
// Replaces the previous 3-second toast — that flashed and vanished
// while the initial Gmail sync was still running in the background,
// leaving the user staring at an empty inbox with no feedback. This
// modal sticks around, counts incoming mail:event:changed frames as
// a live progress chip, and auto-dismisses ~1.5s after the
// MailAccount row flips `initialSyncDone:true`.
//
// Three visible states:
//   1. loading  — spinner + "Connecting your inbox..." + live count
//   2. success  — green check + "Connected!" + brief breather
//   3. timeout  — warning + sync-continues-in-background message
//
// The 5-minute safety timeout never aborts the actual sync (that's
// a server-side job). It just gets the modal out of the user's way
// if Pub/Sub watch setup is delayed or the backfill is unusually
// large. Clicking "Close & continue" at any time does the same:
// dismiss the modal, leave the sync running.
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, CheckCircle2, AlertCircle, X } from 'lucide-react'
import { mailApi } from '../../services/mailApi'
import { subscribeMailEvents } from '../../services/mailSocket'

interface MailConnectingModalProps {
  /** Cuid of the just-connected MailAccount. Matched against `id`
   *  in the /accounts response (string-compared — the legacy
   *  AccountSummary.id type is `number`, the real server payload
   *  is `string`). */
  accountId: string
  /** Fired when the user clicks "Close & continue" OR when the
   *  modal auto-closes after a successful sync. The parent should
   *  reload accounts/labels so the new inbox renders behind it. */
  onClose: () => void
}

// 5 minutes — the user gets the modal out of the way at this point
// even if sync is still running. Backfill of large mailboxes can
// legitimately take longer; we tell the user it continues in the
// background and let them open the dashboard.
const SAFETY_TIMEOUT_MS = 5 * 60 * 1000
// Poll cadence for /accounts to detect the initialSyncDone flip.
// Socket events drive the live counter — polling is just the
// auto-close trigger.
const POLL_INTERVAL_MS = 3000
// Brief pause after success state before auto-close, so the green
// check has time to land in the user's eye rather than flashing by.
const SUCCESS_HOLD_MS = 1500

type ModalState = 'loading' | 'success' | 'timeout'

// Local extension of AccountSummary that includes `initialSyncDone`.
// The shared AccountSummary type in mailApi.ts is legacy (BOS-era,
// id:number, no sync flag) — the actual server response from
// @os/mail's routes/accounts.ts includes this field. Rather than
// touch the cross-cutting type, we cast at the read site.
interface AccountWithSync {
  id: string | number
  initialSyncDone?: boolean
}

export default function MailConnectingModal({ accountId, onClose }: MailConnectingModalProps) {
  const [messageCount, setMessageCount] = useState(0)
  const [state, setState] = useState<ModalState>('loading')
  // Track whether onClose has fired so the safety timeout doesn't
  // also fire after a successful auto-close (would be a no-op but
  // noisy in dev).
  const closedRef = useRef(false)

  // Effect 1 — live message counter from the socket. Every
  // `mail:event:changed` frame with kind:'new' bumps the counter.
  // The backfill that runs right after Connect Google fires one
  // event per imported message, so this naturally tracks progress.
  useEffect(() => {
    const unsub = subscribeMailEvents((e) => {
      if (e.kind === 'new') {
        setMessageCount((n) => n + 1)
      }
    })
    return unsub
  }, [])

  // Effect 2 — poll /accounts every 3s to detect initialSyncDone.
  // When the flag flips, transition to success state and schedule
  // the auto-close. We don't rely on the socket here because the
  // worker writes the flag without emitting a dedicated event;
  // polling is the simplest reliable signal.
  useEffect(() => {
    if (state !== 'loading') return
    let cancelled = false

    const tick = async () => {
      try {
        const r = await mailApi.listAccounts()
        if (cancelled) return
        const accounts = r.data.accounts as unknown as AccountWithSync[]
        const target = accounts.find((a) => String(a.id) === String(accountId))
        if (target?.initialSyncDone) {
          setState('success')
        }
      } catch {
        // Network hiccup — keep polling. The safety timeout is the
        // backstop if we never get a clean response.
      }
    }

    // Immediate first tick so the dashboard isn't blocked on a 3s
    // wait if sync was already complete by the time the modal
    // opened (rare but possible for tiny mailboxes).
    tick()
    const timer = setInterval(tick, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [accountId, state])

  // Effect 3 — auto-close 1.5s after entering success state. The
  // brief hold lets the green check land visually before the modal
  // dismisses; jumping straight from spinner → gone feels jarring.
  useEffect(() => {
    if (state !== 'success') return
    const timer = setTimeout(() => {
      if (closedRef.current) return
      closedRef.current = true
      onClose()
    }, SUCCESS_HOLD_MS)
    return () => clearTimeout(timer)
  }, [state, onClose])

  // Effect 4 — 5-minute safety timeout. Only flips to 'timeout' if
  // we're still loading at that point (a successful auto-close
  // would have already unmounted the component).
  useEffect(() => {
    const timer = setTimeout(() => {
      setState((s) => (s === 'loading' ? 'timeout' : s))
    }, SAFETY_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [])

  const handleClose = () => {
    if (closedRef.current) return
    closedRef.current = true
    onClose()
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60"
      // Backdrop click only dismisses from the timeout state — while
      // sync is running we want the user to actively choose to close
      // (so they're aware the modal isn't just decoration).
      onClick={() => { if (state !== 'loading') handleClose() }}
    >
      <div
        className="relative w-[440px] bg-card rounded-xl shadow-2xl border border-border p-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button only visible in non-loading states — we
            don't want the user dismissing while the sync is still
            mid-burst on the first connect (the X is replaced with
            the "Close & continue" button below in loading state). */}
        {state !== 'loading' && (
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        )}

        {state === 'loading' && (
          <div className="flex flex-col items-center text-center gap-5">
            <div className="w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center">
              <Loader2 size={28} className="text-primary animate-spin" />
            </div>
            <div>
              <div className="text-lg font-semibold text-foreground">Connecting your inbox…</div>
              <p className="text-sm text-muted-foreground mt-1.5">
                We're pulling your recent mail from Gmail. This usually takes a few seconds.
              </p>
            </div>
            <div className="text-xs px-3 py-1.5 rounded-full bg-primary/15 text-primary font-medium">
              {messageCount === 0
                ? 'Starting sync…'
                : `Synced ${messageCount} message${messageCount === 1 ? '' : 's'} so far`}
            </div>
            <button
              onClick={handleClose}
              className="mt-2 text-xs text-muted-foreground hover:text-foreground transition"
            >
              Close & continue
            </button>
          </div>
        )}

        {state === 'success' && (
          <div className="flex flex-col items-center text-center gap-5">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center">
              <CheckCircle2 size={32} className="text-emerald-500" />
            </div>
            <div>
              <div className="text-lg font-semibold text-foreground">Connected!</div>
              <p className="text-sm text-muted-foreground mt-1.5">
                {messageCount > 0
                  ? `${messageCount} message${messageCount === 1 ? '' : 's'} synced. Opening your inbox…`
                  : 'Your inbox is ready. Opening…'}
              </p>
            </div>
          </div>
        )}

        {state === 'timeout' && (
          <div className="flex flex-col items-center text-center gap-5">
            <div className="w-14 h-14 rounded-full bg-amber-500/15 flex items-center justify-center">
              <AlertCircle size={28} className="text-amber-500" />
            </div>
            <div>
              <div className="text-lg font-semibold text-foreground">Still syncing…</div>
              <p className="text-sm text-muted-foreground mt-1.5">
                This is taking longer than usual. Sync continues in the background — your inbox will
                fill in as messages arrive.
              </p>
            </div>
            {messageCount > 0 && (
              <div className="text-xs px-3 py-1.5 rounded-full bg-muted text-muted-foreground">
                {`${messageCount} message${messageCount === 1 ? '' : 's'} synced so far`}
              </div>
            )}
            <button
              onClick={handleClose}
              className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
